import type {
  RouteLocationNormalized,
  RouteLocationRaw,
  RouteRecordRaw,
} from "vue-router";
import { createRouter, createWebHistory } from "vue-router";
import { useAcl } from "vue-simple-acl";
import { getRuntimeConfig } from "~/config/runtime.config";
import {
  PATH_ERROR,
  PATH_NOT_FOUND,
  PATH_UNAUTHORIZED,
} from "~/core/constants/paths";
import { useConfirmLeavePageStore } from "~/core/stores/useConfirmLeavePageStore";
import { isRouteAuthorized } from "~/core/utils/isRouteAuthorized";
import ErrorNotFoundPage from "~/core/views/ErrorNotFoundPage.vue";
import ErrorUnauthorizedPage from "~/core/views/ErrorUnauthorizedPage.vue";
import ErrorUnexpectedPage from "~/core/views/ErrorUnexpectedPage.vue";
import { PATH_ABOUT_ADMINISTRATION } from "~/modules/about/constants";
import aboutRoutes from "~/modules/about/routes";
import { PATH_LOGS } from "~/modules/auditLog/constants";
import logRoutes from "~/modules/auditLog/routes";
import {
  PATH_BUSINESS_UNITS,
  PATH_BUSINESS_UNITS_ID,
} from "~/modules/businessUnit/constants";
import businessUnitRoutes from "~/modules/businessUnit/routes";
import campaignRoutes from "~/modules/campaign/routes";
import { PATH_CARTOGRAPHY } from "~/modules/cartography/constants";
import cartographyRoutes from "~/modules/cartography/routes";
import certificateRoutes from "~/modules/certificate/routes";
import connectMobileAppRoutes from "~/modules/connectMobileApp/routes";
import { PATH_DASHBOARD } from "~/modules/dashboard/constants";
import dashboardRoutes from "~/modules/dashboard/routes";
import { canAccessDashboardPage } from "~/modules/dashboard/utils/canAccessDashboardPage";
import { PATH_DTWINS } from "~/modules/dtwin/constants";
import dtwinRoutes from "~/modules/dtwin/routes";
import firmwareRoutes from "~/modules/firmware/routes";
import { PATH_FLEETS } from "~/modules/fleet/constants";
import fleetRoutes from "~/modules/fleet/routes";
import globalDeviceConfigurationRoutes from "~/modules/globalDeviceConfiguration/routes";
import interfaceRoutes from "~/modules/interface/routes";
import { useGetActiveLicenseInformation } from "~/modules/license/composables/useGetActiveLicenseInformation";
import licenseRoutes from "~/modules/license/routes";
import { PATH_ORGANIZATIONS_ID } from "~/modules/organization/constants";
import organizationRoutes from "~/modules/organization/routes";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";
import { PATH_SECTORS } from "~/modules/sector/constants";
import sectorRoutes from "~/modules/sector/routes";
import { PATH_SUPER_ADMINISTRATION } from "~/modules/superAdministration/constants";
import superAdministrationRoutes from "~/modules/superAdministration/routes";
import {
  PATH_USER_ACCOUNT_ADMINISTRATION,
  PATH_USER_ACCOUNT_ADMINISTRATION_PROFILE,
  PATH_USER_MANAGEMENT_GROUPS,
  PATH_USER_MANAGEMENT_PROFILES,
  PATH_USER_MANAGEMENT_USERS,
} from "~/modules/user/constants";
import { UserSessionEntityType } from "~/modules/user/models/UserSessionEntityType.enum";
import userRoutes from "~/modules/user/routes";
import { useUserSession } from "~/modules/user/stores/useUserSession";

const layoutComponents = import.meta.glob("../core/views/*Layout.vue");

const routes: RouteRecordRaw[] = ([] as RouteRecordRaw[]).concat(
  aboutRoutes,
  businessUnitRoutes,
  campaignRoutes,
  cartographyRoutes,
  certificateRoutes,
  connectMobileAppRoutes,
  dashboardRoutes,
  dtwinRoutes,
  firmwareRoutes,
  fleetRoutes,
  globalDeviceConfigurationRoutes,
  interfaceRoutes,
  licenseRoutes,
  logRoutes,
  organizationRoutes,
  sectorRoutes,
  superAdministrationRoutes,
  userRoutes,
  [
    {
      path: PATH_ERROR,
      component: ErrorUnexpectedPage,
      meta: {
        layout: "ErrorUnexpectedLayout",
      },
    },
    {
      path: PATH_UNAUTHORIZED,
      component: ErrorUnauthorizedPage,
      meta: {
        layout: "ErrorUnauthorizedLayout",
      },
    },
    {
      path: PATH_NOT_FOUND,
      component: ErrorNotFoundPage,
      meta: {
        layout: "ErrorNotFoundLayout",
      },
    },
  ],
);

/**
 * Set up the layouts for each route.
 * Replace the package used before: https://www.npmjs.com/package/vite-plugin-vue-layouts#how-it-works
 * @param routes
 */
function setupLayouts(routes: RouteRecordRaw[]) {
  const routesSetupLayouts = [];
  const mapRoutesByLayout: Map<string, RouteRecordRaw[]> = new Map();

  for (const route of routes) {
    // set route layout to DefaultLayout if not defined
    route.meta = {
      ...(route.meta || {}),
      layout: route.meta?.layout || "DefaultLayout",
    };

    let layoutRoutes = mapRoutesByLayout.get(route.meta.layout!);

    if (!layoutRoutes) {
      layoutRoutes = [];
      mapRoutesByLayout.set(route.meta.layout!, layoutRoutes);
    }

    layoutRoutes.push(route);
  }

  for (const [layout, routes] of mapRoutesByLayout.entries()) {
    const layoutPath = `/${layout}.vue`;
    const layoutComponent =
      Object.entries(layoutComponents).find(([path]) =>
        path.endsWith(layoutPath),
      )?.[1] || layoutComponents["../core/views/DefaultLayout.vue"];

    routesSetupLayouts.push({
      path: "",
      component: layoutComponent,
      children: routes,
    });
  }

  return routesSetupLayouts;
}

/**
 * Set the name of the routes if not set.
 * This enables to use the method "resolve" of the router with the name corresponding to the path.
 * Example: router.resolve({ name: <route.path>, params: { <param1>: <value1>, <param2>: <value2> }})
 * @param routes
 */
function setupNames(routes: RouteRecordRaw[]) {
  for (const route of routes) {
    route.name = route.name ?? route.path;

    if (route.children && route.children.length) {
      route.children = setupNames(route.children);
    }
  }

  return routes;
}

export const router = createRouter({
  history: createWebHistory(getRuntimeConfig().LXC_ROUTER_BASENAME),
  routes: setupLayouts(setupNames(routes)),
});

/**
 * Manage route checks
 * Set current user if needed and clear code/session_state query params
 * Return false if not auth after current user should have been set to avoid showing part of application before auto redirecting to keycloak login
 */
router.beforeEach(
  async (
    to: RouteLocationNormalized,
  ): Promise<RouteLocationNormalized | boolean | void> => {
    const { isAuthenticated, setCurrentUser } = useUserSession();

    // Case when this route needs authentication but the user is not authenticated
    if ((to.meta.auth === undefined || to.meta.auth) && !isAuthenticated) {
      await setCurrentUser();

      delete to.query.code;
      delete to.query.session_state;

      // Set isAuthenticated again to fix infinite loading time
      const { isAuthenticated, userSession } = useUserSession();

      if (isAuthenticated) {
        // Initialize the current organization with the user session one
        if (
          userSession &&
          userSession.entity.type === UserSessionEntityType.OPERATOR
        ) {
          useOrganizationStore().setCurrentOrganization(userSession.entity);
        }

        return to;
      } else {
        return false;
      }
    }

    // For no error page only, set the current user's permissions and check access to the page
    if (!to.meta.layout?.startsWith("Error")) {
      const { userSession, setCurrentUserPermissions } = useUserSession();

      const { currentOrganization } = useOrganizationStore();

      // Set the permissions:
      //    - For administration pages => reset the permissions of the current user
      //    - For other pages => set the permissions of the current organization
      if (to.meta.layout === "AdministrationLayout") {
        if (userSession) {
          setCurrentUserPermissions(userSession?.entity.permissions);
        }
      } else {
        if (currentOrganization) {
          setCurrentUserPermissions(currentOrganization?.permissions);
        }
      }

      const acl = useAcl();

      // Route to redirect to when the current page is not authorized, ordered by priority
      // // it depends on the user entity type
      let redirectRoutes: RouteLocationRaw[] = [];

      // Redirection depending on the user's organization type
      // The use of "return false" is required in order to stop the check of the ACL of the previous route
      switch (userSession?.entity.type) {
        // A user of the super administration can only access to the administration pages
        case UserSessionEntityType.SUPER_ADMINISTRATION: {
          if (to.path === PATH_DASHBOARD) {
            await router.replace(PATH_SUPER_ADMINISTRATION);
            return false;
          } else if (to.meta.layout !== "AdministrationLayout") {
            await router.replace(PATH_UNAUTHORIZED);
            return false;
          }

          redirectRoutes = router
            .getRoutes()
            // take only the routes of the super administration
            .filter((route) =>
              route.path.startsWith(PATH_USER_ACCOUNT_ADMINISTRATION),
            )
            .map((route) => route.path)
            .concat([
              PATH_USER_ACCOUNT_ADMINISTRATION_PROFILE,
              PATH_ABOUT_ADMINISTRATION,
            ]);

          break;
        }

        // a user of a business unit can access to the operation pages if he has a delegation
        // or to the administration pages except the super administration ones
        case UserSessionEntityType.BUSINESS_UNIT: {
          if (to.meta.layout === "DefaultLayout" && !currentOrganization) {
            await router.replace({
              name: PATH_BUSINESS_UNITS_ID,
              params: { id: userSession.entity.code },
            });
            return false;
          } else if (to.path.startsWith(PATH_SUPER_ADMINISTRATION)) {
            await router.replace(PATH_UNAUTHORIZED);
            return false;
          }

          redirectRoutes = router
            .getRoutes()
            // take only the routes of the business unit containing no other parameters
            .filter(
              (route) =>
                route.path.startsWith(PATH_BUSINESS_UNITS_ID) &&
                !route.path.replace(PATH_BUSINESS_UNITS_ID, "").includes(":"),
            )
            .map(
              (route) =>
                ({
                  name: route.path,
                  params: { id: userSession.entity.code },
                }) as RouteLocationRaw,
            )
            .concat([
              PATH_USER_ACCOUNT_ADMINISTRATION_PROFILE,
              PATH_ABOUT_ADMINISTRATION,
            ]);

          break;
        }

        // a user of an administration can access to the operation pages if he has a delegation
        // or to the administration pages except the super administration and the business units ones
        case UserSessionEntityType.ADMINISTRATION: {
          if (to.meta.layout === "DefaultLayout" && !currentOrganization) {
            await router.replace({
              name: PATH_ORGANIZATIONS_ID,
              params: { id: userSession.entity.code },
            });
            return false;
          } else if (
            to.path.startsWith(PATH_SUPER_ADMINISTRATION) ||
            to.path.startsWith(PATH_BUSINESS_UNITS)
          ) {
            await router.replace(PATH_UNAUTHORIZED);
            return false;
          }

          redirectRoutes = router
            .getRoutes()
            // take only the routes of the organization containing no other parameters
            .filter(
              (route) =>
                route.path.startsWith(PATH_ORGANIZATIONS_ID) &&
                !route.path.replace(PATH_BUSINESS_UNITS_ID, "").includes(":"),
            )
            .map(
              (route) =>
                ({
                  name: route.path,
                  params: { id: userSession.entity.code },
                }) as RouteLocationRaw,
            )
            .concat([
              PATH_USER_ACCOUNT_ADMINISTRATION_PROFILE,
              PATH_ABOUT_ADMINISTRATION,
            ]);

          break;
        }

        // a user of an operation organization can only access to the operation pages
        // and the organization administration if he has the permissions
        case UserSessionEntityType.OPERATOR: {
          if (
            to.path.startsWith(PATH_SUPER_ADMINISTRATION) ||
            to.path.startsWith(PATH_BUSINESS_UNITS)
          ) {
            await router.replace(PATH_UNAUTHORIZED);
            return false;
          }

          // Check if the dashboard is accessible
          if (to.path === PATH_DASHBOARD) {
            // Cannot access to the dashboard => redirect to the device list
            const { getActiveLicenseInformation } =
              useGetActiveLicenseInformation({
                organizationCode: currentOrganization?.code,
              });

            await getActiveLicenseInformation();

            if (!canAccessDashboardPage()) {
              await router.replace(PATH_DTWINS);
              return false;
            }
          }

          redirectRoutes = [
            ...new Set(
              [
                PATH_LOGS,
                PATH_DTWINS,
                PATH_CARTOGRAPHY,
                PATH_FLEETS,
                PATH_SECTORS,
                PATH_USER_MANAGEMENT_USERS,
                PATH_USER_MANAGEMENT_GROUPS,
                PATH_USER_MANAGEMENT_PROFILES,
              ]
                // Add the other operation pages except the dashboard one and those with parameters
                .concat(
                  router
                    .getRoutes()
                    .filter(
                      (route) =>
                        route.meta.layout === "DefaultLayout" && // Only keep operation pages
                        !route.path.includes(":"), // Only keep path without parameter
                    )
                    .map((route) => route.path)
                    .filter((path) => path !== PATH_DASHBOARD),
                ),
            ),
          ];
          break;
        }
      }

      // If the current page is not authorized, redirect to the first authorized page
      if (
        typeof to.meta.can === "function" &&
        !(await to.meta.can(to, undefined, acl.can))
      ) {
        for (const redirectRoute of redirectRoutes) {
          if (isRouteAuthorized(redirectRoute, router)) {
            await router.replace(redirectRoute);
            return false;
          }
        }

        await router.push(PATH_UNAUTHORIZED);
        return false;
      }
    }

    // case checking display confirm
    const confirmLeavePageStore = useConfirmLeavePageStore();

    // need confirm before leaving the page
    if (confirmLeavePageStore.isActivated(to)) {
      // let redirection pass if it is done after confirm
      if (confirmLeavePageStore.confirmed()) {
        confirmLeavePageStore.reset();
      }
      // otherwise display the leaving page confirm message
      else {
        // provide the destination route in order to redirect after confirm
        confirmLeavePageStore.show(to.fullPath);
        return false; // stop redirection for confirm
      }
    }
  },
);
